import { supabase } from '../supabase';
import type { ProfilePrefill } from '../domain/profilePrefill';
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
  isSupportedLanguage,
} from '@/lib/i18n/constants';

// Re-export for backward compatibility
export { SUPPORTED_LANGUAGES, type SupportedLanguage, isSupportedLanguage };

type RawProfile = Record<string, unknown>;

function asString(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v);
}

function joinName(first: string, last: string): string {
  const full = `${first} ${last}`.trim();
  return full;
}

/**
 * Fetch the current user's profile and map it to the fields used by ProjectData ("my_*").
 * Returns an empty object if profile is missing or cannot be loaded.
 */
export async function loadProfilePrefill(userId: string): Promise<ProfilePrefill> {
  const { data, error } = await supabase
    .from('profiles')
    .select(
      [
        'name',
        'display_name',
        'first_name',
        'last_name',
        'company_name',
        'ice',
        'rc',
        'patente',
        'ifiscale',
        'tel',
        'email',
      ].join(',')
    )
    .eq('id', userId)
    .single();

  if (error || !data) return {};

  const p: RawProfile = typeof data === 'object' && data !== null ? (data as RawProfile) : {};

  const first = asString(p.first_name);
  const last = asString(p.last_name);
  const fullName = joinName(first, last) || asString(p.name) || asString(p.display_name);

  return {
    my_name: fullName,
    my_company: asString(p.company_name),
    my_email: asString(p.email),
    my_phone: asString(p.tel),
    my_ICE: asString(p.ice),
    my_RC: asString(p.rc),
    my_Patente: asString(p.patente),
    my_IF: asString(p.ifiscale),
  };
}

export async function getUserLanguage(userId: string): Promise<SupportedLanguage | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('language')
    .eq('id', userId)
    .single();

  if (error || !data) return null;
  const raw = (data as RawProfile).language;
  return isSupportedLanguage(raw) ? raw : null;
}

export async function setUserLanguage(userId: string, language: SupportedLanguage): Promise<void> {
  const { error } = await supabase.from('profiles').update({ language }).eq('id', userId);

  if (error) throw error;
}
